import React, { useState, useCallback, useEffect } from "react";
import { RefreshCw, Users, Settings, AlertTriangle, Play } from "lucide-react";
import { useApi } from "../hooks/useApi";
import { useRBAC } from "../hooks/useRBAC";
import Navigation from "./Navigation";
import WieExecutiveHeader from "./wie/WieExecutiveHeader";
import WieSkillTable from "./wie/WieSkillTable";
import WieHeadcountSetup from "./wie/WieHeadcountSetup";
import WieScenarioPanel from "./wie/WieScenarioPanel";

// ── Target clearance window selector ─────────────────────────────────────────
function HorizonSelector({ value, onChange }) {
  return (
    <div
      className="flex items-center gap-1.5 bg-gray-100 rounded-xl p-1"
      title="Target clearance window — the deadline to clear your ENTIRE open backlog by. This is NOT an SLA filter (it doesn't show 'only what's due in 30 days'): every finding is still held to its own severity SLA (Critical 1mo, High 2mo). A longer window lowers Required FTE, but urgent work never looks cheaper."
    >
      {[30, 90, 180].map(d => (
        <button
          key={d}
          onClick={() => onChange(d)}
          className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors
            ${value === d
              ? "bg-white text-gray-900 shadow-sm"
              : "text-gray-500 hover:text-gray-700"
            }`}
        >
          {d}d
        </button>
      ))}
    </div>
  );
}

// ── Mode toggle ───────────────────────────────────────────────────────────────
function ModeToggle({ mode, onChange, hasCapacity }) {
  return (
    <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1">
      <button
        onClick={() => onChange("WORKLOAD_ONLY")}
        className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors
          ${mode === "WORKLOAD_ONLY"
            ? "bg-white text-gray-900 shadow-sm"
            : "text-gray-500 hover:text-gray-700"
          }`}
      >
        Workload Only
      </button>
      <button
        onClick={() => onChange("GAP_ANALYSIS")}
        className={`flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors
          ${mode === "GAP_ANALYSIS"
            ? "bg-white text-gray-900 shadow-sm"
            : "text-gray-500 hover:text-gray-700"
          }`}
      >
        Gap Analysis
        {!hasCapacity && <AlertTriangle className="w-3 h-3 text-amber-500" />}
      </button>
    </div>
  );
}

// ── Empty / Error states ──────────────────────────────────────────────────────
function EmptyState({ onRun }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mb-4">
        <Users className="w-7 h-7 text-blue-400" />
      </div>
      <h3 className="text-base font-bold text-gray-900 mb-1">No forecast yet</h3>
      <p className="text-sm text-gray-500 mb-6 max-w-sm">
        Run your first workforce forecast to see how much security capacity you need
        to address your open risk backlog.
      </p>
      <button
        onClick={onRun}
        className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors"
      >
        <RefreshCw className="w-4 h-4" />
        Run Forecast
      </button>
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────────
export default function WorkforceDashboard() {
  const apiCall = useApi();
  const { canManageWorkforceIntelligence } = useRBAC();

  const [forecast, setForecast]         = useState(null);
  const [loading,  setLoading]          = useState(true);
  const [running,  setRunning]          = useState(false);
  const [error,    setError]            = useState(null);
  const [horizonDays, setHorizonDays]   = useState(90);
  const [mode,     setMode]             = useState("WORKLOAD_ONLY");
  const [capacityData, setCapacityData] = useState(null);
  const [wieSettings,  setWieSettings]  = useState(null);
  const [savingCap,    setSavingCap]    = useState(false);
  const [showSetup,    setShowSetup]    = useState(false);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [showScenario,  setShowScenario]  = useState(false);
  const [scenarioResult, setScenarioResult] = useState(null);
  const [runningScenario, setRunningScenario] = useState(false);

  const orgId = JSON.parse(localStorage.getItem("selectedOrganization") || "{}").organizationId;
  const canManage = canManageWorkforceIntelligence?.(orgId);

  // ── Load latest forecast + settings + capacity on mount / horizon change ──
  const loadData = useCallback(async (horizon) => {
    setLoading(true);
    setError(null);
    try {
      const [fc, settings, cap] = await Promise.all([
        apiCall("GET", `/workforce/forecast/latest?horizonDays=${horizon}`).catch(() => null),
        apiCall("GET", "/workforce/settings").catch(() => null),
        apiCall("GET", "/workforce/capacity").catch(() => null),
      ]);
      if (fc) setForecast(fc);
      if (settings?.mode) setMode(settings.mode);
      if (settings) setWieSettings(settings);
      if (cap) setCapacityData(cap);
    } catch (e) {
      setError("Failed to load workforce data.");
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => { loadData(horizonDays); }, [horizonDays]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Run forecast ──────────────────────────────────────────────────────────
  const runForecast = useCallback(async () => {
    setRunning(true);
    setError(null);
    try {
      const result = await apiCall("POST", "/workforce/forecast", { horizonDays });
      setForecast(result);
    } catch {
      setError("Forecast failed. Please try again.");
    } finally {
      setRunning(false);
    }
  }, [apiCall, horizonDays]);

  // ── Change horizon ────────────────────────────────────────────────────────
  const handleHorizonChange = (d) => {
    setHorizonDays(d);
    setSelectedSkill(null);
  };

  // ── Change mode + persist ─────────────────────────────────────────────────
  const handleModeChange = async (newMode) => {
    setMode(newMode);
    setSelectedSkill(null);
    try {
      // Preserve existing settings (don't let omitted fields reset automation maturity / benchmark),
      // then re-run so the displayed forecast matches the selected mode (gap-only sections need a gap run).
      await apiCall("PUT", "/workforce/settings", {
        ...(wieSettings || {}),
        mode: newMode,
        defaultHorizon: horizonDays,
        aiEnabled: wieSettings?.aiEnabled ?? true,
      });
      await runForecast();
    } catch { setError("Failed to switch mode."); }
  };

  // ── Run scenario ─────────────────────────────────────────────────────────
  const handleRunScenario = useCallback(async (req) => {
    setRunningScenario(true);
    try {
      const result = await apiCall("POST", "/workforce/scenario", req);
      setScenarioResult(result);
    } catch {
      setError("Scenario simulation failed. Please try again.");
    } finally {
      setRunningScenario(false);
    }
  }, [apiCall]);

  // ── Save capacity ─────────────────────────────────────────────────────────
  const handleSaveCapacity = async (skills, modelSettings) => {
    setSavingCap(true);
    try {
      await apiCall("PUT", "/workforce/capacity", { skills });
      setCapacityData(skills.map(s => ({ ...s, skillArea: s.skillArea })));
      if (modelSettings) {
        await apiCall("PUT", "/workforce/settings", {
          mode,
          defaultHorizon: horizonDays,
          aiEnabled: true,
          automationMaturity:            modelSettings.automationMaturity,
          historicalValidationCertified: modelSettings.historicalValidationCertified,
        });
      }
      setShowSetup(false);
      // Re-run forecast to incorporate new capacity and model settings
      await runForecast();
    } catch {
      setError("Failed to save capacity.");
    } finally {
      setSavingCap(false);
    }
  };

  // ── Derive active forecast data ───────────────────────────────────────────
  // Backend runs one horizon at a time; re-fetch on horizon change
  // Deduplicate by skillArea — guards against multi-region double-load of the same skill
  const activeSkills = [...new Map((forecast?.skills ?? []).map(s => [s.skillArea, s])).values()];
  const activeExec     = forecast?.executive ?? null;
  // hasCapacity: true when ANY capacity source is configured (FTE, contractor, or outsourced hours)
  // Fix: previously only checked currentFte, which showed false-alarm warning even when contractor hours were set
  const hasCapacity    = capacityData?.some(c => c.currentFte > 0 || c.contractorHours > 0 || c.outsourcedHours > 0);

  // ── Attach loadedAnnualCost (for the FTE budget in the drilldown) ──────────
  // availableFte / gapFte / coveragePercent are taken straight from the backend, which computes
  // them at the canonical 109 h/FTE/mo and already folds in contractor, outsourced, and planned-hire
  // capacity. No frontend re-derivation — that previously drifted from the engine (review C2).
  const enrichedSkills = activeSkills.map(skill => {
    const cap = capacityData?.find(c => c.skillArea === skill.skillArea);
    return { ...skill, loadedAnnualCost: cap?.loadedAnnualCost || 0 };
  });

  // ── FTE Budget: sum of (requiredFte × loadedAnnualCost) across all skills ──
  const totalRequiredBudget = enrichedSkills.reduce(
    (sum, s) => sum + ((s.requiredFte || 0) * (s.loadedAnnualCost || 0)), 0
  );

  // ── Executive summary coverage (gap mode) ─────────────────────────────────
  // Use effective-coverage formula: Σ min(available, required) / Σ required.
  // This prevents a surplus in one skill from masking a deficit in another,
  // keeping Coverage and Gap mathematically consistent (Gap > 0 → Coverage < 100%).
  const gapModeActive = mode === "GAP_ANALYSIS" && hasCapacity;
  let execCoverageOverride = {};
  if (gapModeActive && enrichedSkills.length > 0) {
    const totalRequiredFte      = enrichedSkills.reduce((s, k) => s + (k.requiredFte  || 0), 0);
    const effectiveCoveredFte   = enrichedSkills.reduce(
      (s, k) => s + Math.min(k.availableFte || 0, k.requiredFte || 0), 0
    );
    const weightedCoverage = totalRequiredFte > 0
      ? Math.min(100, (effectiveCoveredFte / totalRequiredFte) * 100)
      : 0;
    execCoverageOverride = {
      coveragePercent: weightedCoverage,
      totalGapFte: enrichedSkills.reduce((s, k) => s + (k.gapFte || 0), 0),
    };
  }

  const execWithGap = activeExec ? {
    ...activeExec,
    totalRequiredBudget,
    ...execCoverageOverride,
  } : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Loading workforce intelligence…</p>
        </div>
      </div>
    );
  }

  return (
    <>
    <Navigation />
    <div className="max-w-6xl mx-auto px-4 py-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-gray-900">Workforce Intelligence</h1>
            <img
              src={`${process.env.PUBLIC_URL || ""}/beta-badge.svg`}
              alt="Beta"
              className="h-5 w-auto select-none"
            />
          </div>
          <p className="text-sm text-gray-500 mt-0.5">
            Security capacity required to address your open risk backlog
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <HorizonSelector value={horizonDays} onChange={handleHorizonChange} />
          {canManage && (
            <>
              <ModeToggle mode={mode} onChange={handleModeChange} hasCapacity={hasCapacity} />
              {mode === "GAP_ANALYSIS" && (
                <button
                  onClick={() => setShowSetup(true)}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  <Settings className="w-3.5 h-3.5" />
                  Team Setup
                </button>
              )}
              {forecast && (
                <button
                  onClick={() => { setShowScenario(true); setScenarioResult(null); }}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-purple-700 bg-purple-50 border border-purple-200 rounded-xl hover:bg-purple-100 transition-colors"
                >
                  <Play className="w-3.5 h-3.5" />
                  Scenario
                </button>
              )}
              <button
                onClick={runForecast}
                disabled={running}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${running ? "animate-spin" : ""}`} />
                {running ? "Running…" : "Refresh"}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mb-4 flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700 underline text-xs">
            Dismiss
          </button>
        </div>
      )}

      {/* Gap Analysis warning if no capacity set */}
      {mode === "GAP_ANALYSIS" && !hasCapacity && (
        <div className="mb-4 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-700">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          Gap Analysis mode requires team capacity data.&nbsp;
          <button onClick={() => setShowSetup(true)} className="underline font-semibold">
            Configure your team →
          </button>
        </div>
      )}

      {/* Main content */}
      {!forecast ? (
        <EmptyState onRun={runForecast} />
      ) : (
        <>
          {/* Level 1: Executive Header */}
          <WieExecutiveHeader
            executive={execWithGap}
            mode={mode}
            horizonDays={horizonDays}
            totalFindings={forecast?.totalFindings}
            uniqueFindings={forecast?.uniqueFindings}
            duplicatesRemoved={forecast?.duplicatesRemoved}
            skills={enrichedSkills}
            methodologyVersion={forecast?.methodologyVersion}
            parametersJson={forecast?.parametersJson}
          />

          {/* Level 2: Skill Table */}
          <WieSkillTable
            skills={enrichedSkills}
            mode={mode}
            onSelectSkill={setSelectedSkill}
            selectedSkill={selectedSkill}
            horizonDays={horizonDays}
            apiCall={apiCall}
            canManage={canManage}
            onOverrideSaved={runForecast}
          />

          {/* Forecast metadata */}
          {forecast.runId && (
            <p className="text-xs text-gray-400 text-center mt-2">
              Forecast ID: {forecast.runId}
              {forecast.createdAt && ` · Generated ${new Date(forecast.createdAt).toLocaleString()}`}
            </p>
          )}
        </>
      )}

      {/* Headcount Setup Modal */}
      {showSetup && (
        <WieHeadcountSetup
          capacityData={capacityData}
          settings={wieSettings}
          onSave={handleSaveCapacity}
          onClose={() => setShowSetup(false)}
          saving={savingCap}
        />
      )}

      {/* Scenario Planning Modal */}
      {showScenario && (
        <WieScenarioPanel
          onClose={() => setShowScenario(false)}
          onRun={handleRunScenario}
          running={runningScenario}
          result={scenarioResult}
          horizonDays={horizonDays}
        />
      )}
    </div>
    </>
  );
}
